import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlassIcon, UserIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { clientService } from '../../services/clientService';
import { cn } from '../../utils/cn';

const ClientSearchSelect = ({ 
  value, 
  onChange, 
  placeholder = "Buscar cliente...", 
  error = null,
  required = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const hasValue = value !== '' && value != null;

  // Al editar una licencia hay valor antes de abrir el dropdown: cargar ese cliente para mostrar nombre/email
  const { data: clientById, isLoading: loadingClientById } = useQuery({
    queryKey: ['client-detail', value],
    queryFn: () => clientService.getClient(value),
    enabled: hasValue,
  });

  // Lista al abrir el desplegable (búsqueda)
  const { data: clientsRaw, isLoading: loadingList } = useQuery({
    queryKey: ['clients-search', searchTerm],
    queryFn: () => clientService.getClients({ search: searchTerm }),
    enabled: isOpen,
  });

  const clients = Array.isArray(clientsRaw)
    ? clientsRaw
    : clientsRaw?.results ?? [];

  // Filter clients based on search term
  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;
    return clients.filter(client => 
      client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.company?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [clients, searchTerm]);

  const selectedClient = useMemo(() => {
    if (clientById) return clientById;
    return clients.find(client => String(client.id) === String(value));
  }, [clientById, clients, value]);

  const isLoading = loadingClientById || loadingList;

  const handleSelect = (client) => {
    onChange(client.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange('');
    setSearchTerm('');
  };

  return (
    <div className={cn("relative", className)}>
      {/* Input Field */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <UserIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={selectedClient ? `${selectedClient.name} - ${selectedClient.email}` : searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className={cn(
            "w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            error ? "border-red-300" : "border-gray-300"
          )}
          required={required}
        />
        
        {/* Clear button */}
        {selectedClient && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="p-3 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm">Buscando clientes...</p>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="p-3 text-center text-gray-500">
              <p className="text-sm">No se encontraron clientes</p>
              {searchTerm && (
                <p className="text-xs mt-1">Intenta con otro término de búsqueda</p>
              )}
            </div>
          ) : (
            <div className="py-1">
              {filteredClients.map((client) => (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => handleSelect(client)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <UserIcon className="h-4 w-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {client.name}
                      </p>
                      <div className="flex items-center text-xs text-gray-500">
                        <BuildingOfficeIcon className="h-3 w-3 mr-1" />
                        <span className="truncate">{client.company}</span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {client.email}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <span className={cn(
                        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                        client.status === 'active' 
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      )}>
                        {client.status === 'active' ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export { ClientSearchSelect };
